import React, { useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Stack,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    useTheme
} from "@mui/material";
import {
    MoreVert as MoreVertIcon,
    ThumbUp as ThumbUpIcon,
    ThumbsUpDown as ThumbsUpDownIcon,
    ThumbDownAlt as ThumbDownAltIcon,
    StarBorder as StarBorderIcon,
    Star as StarIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Rule as RuleIcon,
    Payments as PaymentsIcon,
} from "@mui/icons-material";

import { SegmentedProgressBar } from "../../../shared/components";
import { ProjectCardProps } from "../types";

export const ProjectCard: React.FC<ProjectCardProps> = ({
    title,
    location,
    workType,
    approvalStage,
    progress,
    projectId,
    phases,
    ragStatus,
    isPinned,
    handleClick,
    handlePinToggle,
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderTopWidth: "4px",
                borderTopStyle: "solid",
                borderTopColor:
                    ragStatus.overall === "green"
                        ? "success.main"
                        : ragStatus.overall === "amber"
                            ? "warning.main"
                            : "error.main",
                height: "100%",
                cursor: "pointer",
            }}
            onClick={handleClick}
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="body2"
                        sx={{
                            maxWidth: "70%",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip
                            title={
                                <Box
                                    sx={{
                                        p: 0.5,
                                        py: 1,
                                        display: "flex",
                                        gap: 1,
                                        bgcolor: theme.palette.background.default,
                                    }}
                                >
                                    <CheckCircleIcon sx={{ color: "success.main", fontSize: "1rem" }} />
                                    <Typography variant="body2">
                                        <b>Overall Project RAG:</b> Based on <b>Cost, Time,</b> and <b>Quality</b> performance. Red overrides Amber, Amber overrides Green.
                                    </Typography>
                                </Box>
                            }
                            arrow
                            placement="top-start"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: theme.palette.background.default,
                                        color: "text.primary",
                                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.32)",
                                        borderRadius: "8px",
                                        maxWidth: "300px",
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: theme.palette.background.default,
                                        fontSize: "1rem",
                                    },
                                },
                            }}
                        >
                            {ragStatus.overall === "green" ? (
                                <ThumbUpIcon sx={{ color: "success.main", fontSize: 20 }} />
                            ) : ragStatus.overall === "amber" ? (
                                <ThumbsUpDownIcon sx={{ color: "warning.main", fontSize: 20 }} />
                            ) : (
                                <ThumbDownAltIcon sx={{ color: "error.main", fontSize: 20 }} />
                            )}
                        </Tooltip>

                        {/* ⋮ Menu Button */}
                        <IconButton size="small" onClick={handleMenuOpen}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>

                        {/* Menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleMenuClose();
                                    handlePinToggle(!isPinned);
                                }}
                            >
                                {isPinned ? (
                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                        <StarIcon sx={{ color: "primary.main"}} fontSize="small" />
                                        <Typography variant="subtitle2">
                                            Unpin Project
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                        <StarBorderIcon sx={{ color: "primary.main"}} fontSize="small" />
                                        <Typography variant="subtitle2">
                                            Pin Project
                                        </Typography>
                                    </Box>
                                )}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{
                    overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"
                }}>
                    {location}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{
                    my: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"
                }}>
                    {workType}
                </Typography>

                <Chip
                    sx={{ borderRadius: 1 }}
                    label={approvalStage}
                    size="small"
                    variant="filled"
                />

                <Box display="flex" alignItems="center" gap={1} sx={{ my: 1 }}>
                    <SegmentedProgressBar
                        phases={phases}
                        progressPercentage={progress}
                        status={ragStatus.overall}
                    />
                </Box>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                        {projectId}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems={"stretch"}>
                        <PaymentsIcon
                            sx={{
                                fontSize: 22,
                                color:
                                    ragStatus.cost === "amber"
                                        ? "warning.main"
                                        : ragStatus.cost === "green"
                                            ? "success.main"
                                            : "error.main",
                            }}
                        />
                        <AccessTimeIcon
                            sx={{
                                fontSize: 22,
                                color:
                                    ragStatus.time === "amber"
                                        ? "warning.main"
                                        : ragStatus.time === "green"
                                            ? "success.main"
                                            : "error.main",
                            }}
                        />
                        <RuleIcon
                            sx={{
                                fontSize: 22,
                                color:
                                    ragStatus.quality === "amber"
                                        ? "warning.main"
                                        : ragStatus.quality === "green"
                                            ? "success.main"
                                            : "error.main",
                            }}
                        />
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};